/**
 * BugReportButton — floating "Report a Bug" button + modal.
 *
 * - Renders a tiny corner button on every authenticated page.
 * - Click opens a modal: severity selector, description textarea.
 * - On submit, auto-captures current URL + user-agent and POSTs to
 *   /api/bug-report.  Backend persists to MongoDB and emails the report
 *   straight to every SuperAdmin.
 * - Designed for beta-tester feedback collection — no friction, no fields
 *   the user has to remember.
 */
import React, { useState, useCallback } from 'react';
import { Bug, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../lib/authFetch';

const SEVERITY_OPTIONS = [
  { value: 'low',    label: 'Low',    desc: 'Minor: UI glitch, typo, cosmetic',     dot: 'bg-emerald-500' },
  { value: 'medium', label: 'Medium', desc: 'Annoying: confusing or partly broken', dot: 'bg-amber-500'   },
  { value: 'high',   label: 'High',   desc: 'Blocking: cannot use the app',         dot: 'bg-rose-500'    },
];

export default function BugReportButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState('medium');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    const desc = description.trim();
    if (desc.length < 5) {
      toast.error('Please describe the issue in at least a few words.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/bug-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: desc,
          severity,
          page_url: typeof window !== 'undefined' ? window.location.href : '',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success('Thank you: report sent to the team.');
        setOpen(false);
        setDescription('');
        setSeverity('medium');
      } else {
        toast.error(j?.detail || 'Could not submit the report. Please try again.');
      }
    } catch (_) {
      toast.error('Network error: please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  }, [description, severity]);

  // Only show to authenticated users.
  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 bg-zinc-900 text-white rounded-full pl-3 pr-4 py-2.5 shadow-lg hover:bg-zinc-700 hover:shadow-xl transition-all"
        aria-label="Report a bug"
        title="Report a bug"
        data-testid="bug-report-fab"
      >
        <Bug className="w-4 h-4" strokeWidth={2.4} />
        <span className="text-xs font-semibold uppercase tracking-wider">Report Bug</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="bug-report-modal"
        >
          <div className="bg-white w-full sm:max-w-lg sm:rounded-lg rounded-t-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <div>
                <h2 className="text-base font-bold text-zinc-950">Report a Bug</h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Goes straight to the team: usually triaged within 24 hours.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                aria-label="Close"
                data-testid="bug-report-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 mb-2 block">
                  How bad is it?
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {SEVERITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSeverity(opt.value)}
                      className={`p-2.5 rounded-md border text-left transition-colors ${
                        severity === opt.value
                          ? 'border-zinc-900 bg-zinc-50'
                          : 'border-zinc-200 hover:border-zinc-400'
                      }`}
                      data-testid={`bug-severity-${opt.value}`}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`w-2 h-2 rounded-full ${opt.dot}`} />
                        <span className="text-xs font-bold text-zinc-950">{opt.label}</span>
                      </div>
                      <p className="text-[10px] text-zinc-500 leading-tight">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label
                  htmlFor="bug-description"
                  className="text-xs font-semibold uppercase tracking-wider text-zinc-700 mb-2 block"
                >
                  What happened?
                </Label>
                <Textarea
                  id="bug-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what you were doing, what you expected and what actually happened…"
                  rows={5}
                  maxLength={5000}
                  className="resize-none text-sm"
                  required
                  data-testid="bug-description-input"
                />
                <div className="flex justify-between mt-1 text-[11px] text-zinc-500">
                  <span>The current page URL and your browser info are sent automatically.</span>
                  <span>{description.length}/5000</span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  data-testid="bug-report-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-zinc-900 hover:bg-zinc-700 text-white min-w-[120px]"
                  disabled={submitting || description.trim().length < 5}
                  data-testid="bug-report-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    'Send Report'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
